import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import { useSnackbar } from "notistack";
import { useRef } from "react";
import { useMutation } from "react-query";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import { pickProfileReturnUrl } from "../../utils/profileReturnUrl";

function DeleteMyAccount(props) {
  const { params, openDeleteAccount, setOpenDeleteAccount, signOut } = props;

  const signOutInitiated = useRef(false);

  const axiosPrivate = useAxiosPrivate();

  const { enqueueSnackbar } = useSnackbar();

  const redirectToLoginWithDelete = () => {
    // Carry tenant_id so external-browser users land on the correct tenant login.
    const tidParam =
      new URLSearchParams(window.location.search).get("tenant_id") ||
      new URLSearchParams(window.location.search).get("tenantId");
    try {
      const loginUrl = new URL("/login", window.location.origin);
      loginUrl.searchParams.set("delete", "delete");
      if (tidParam) loginUrl.searchParams.set("tenant_id", tidParam);
      window.location.href = loginUrl.toString();
    } catch {
      window.location.href = tidParam
        ? `/login?tenant_id=${tidParam}&delete=delete`
        : "/login?delete=delete";
    }
  };

  const { mutate, isLoading } = useMutation(() => axiosPrivate.delete(`/profile`), {
    onSuccess: async (response) => {
      const status = response?.status;
      if (status < 200 || status >= 300 || signOutInitiated.current) return;

      signOutInitiated.current = true;
      setOpenDeleteAccount(false);
      enqueueSnackbar("Successfully deleted your account.", {
        variant: "success",
      });

      const redirectUrl = pickProfileReturnUrl(params?.deeplink, params?.redirect_uri);
      if (redirectUrl) {
        redirectUrl.searchParams.set("delete", "delete");
        window.location.href = redirectUrl.toString();
        return;
      }

      try {
        if (typeof signOut === "function") {
          await signOut();
          return;
        }
        redirectToLoginWithDelete();
      } catch (error) {
        console.error("Sign out error:", error);
        redirectToLoginWithDelete();
      }
    },
    onError: (error) => {
      enqueueSnackbar(
        error?.response?.data?.error?.message ||
          error?.message ||
          "Network Error!",
        {
          variant: "error",
        }
      );
    },
  });

  return (
    <Dialog open={openDeleteAccount} maxWidth="xs">
      <DialogTitle
        id="delete-account-dialog-title"
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          bgcolor: "#fafafa",
        }}
      >
        <Typography
          sx={{
            textAlign: "center",
            fontSize: 18,
            fontWeight: "bold",
          }}
        >
          Confirm Delete Account Operation
        </Typography>
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ ml: 1, mt: 2 }}>
          <Typography>
            Are you sure you want delete your SSO account?
          </Typography>
          <Typography>This is an irriversible operation!</Typography>
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
            mx: 2,
            pb: 1,
          }}
        >
          <Button
            onClick={() => setOpenDeleteAccount(false)}
            sx={{
              bgcolor: "black",
              ":hover": { bgcolor: "black" },
              color: "#fff",
              textTransform: "none",
              borderRadius: 6,
              fontWeight: "bold",
              mb: { xs: 1, md: 0 },
              mr: { xs: 0, md: 1 },
            }}
            size="large"
            fullWidth
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            sx={{
              bgcolor: "red",
              ":hover": {
                bgcolor: "red",
              },
              color: "#fff",
              textTransform: "none",
              borderRadius: 6,
              fontWeight: "bold",
            }}
            onClick={() => mutate()}
            size="large"
            fullWidth
            disabled={isLoading}
          >
            Confirm
            {isLoading && (
              <CircularProgress
                size={20}
                sx={{
                  color: "#fff",
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  marginTop: "-12px",
                  marginLeft: "-12px",
                }}
              />
            )}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
export default DeleteMyAccount;
